import React, { useCallback, useMemo } from 'react';
import { BubbleMenu } from '@tiptap/react';
import { Node } from '@tiptap/pm/model';
import { Editor } from '@tiptap/core';
import { Rect } from '@popperjs/core';
import { Placement } from 'tippy.js';

import { BubbleMenuIconButton, BubbleMenuToolbar } from '../..';
import { IconMap } from '../../../../shared/sprite';
import { useTiptapEditor } from '../../lib';
import { calculateBubbleMenuOffset, getNodeByType } from './lib';

function getPdfNode(editor: Editor) {
  return getNodeByType(editor, 'pdfComponent');
}

export const TiptapPdfBubbleMenu = () => {
  const { editor } = useTiptapEditor();
  const selection = editor?.state.selection;
  const isPdfComponentActive = editor?.isActive('pdfComponent');
  const node = useMemo((): Node | null => {
    if (!editor || !isPdfComponentActive) return null;
    return getPdfNode(editor).node;
  }, [editor, isPdfComponentActive, selection]);

  const calculateOffset = useCallback(
    ({
      popper,
      reference,
      placement,
    }: {
      popper: Rect;
      reference: Rect;
      placement: Placement;
    }): [number, number] => {
      if (!editor) return [0, 0];
      return calculateBubbleMenuOffset(editor, 'pdfComponent', {
        popper,
        reference,
        placement,
      });
    },
    [editor]
  );

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      tippyOptions={{
        duration: 200,
        placement: 'top-start',
        maxWidth: 'none',
        offset: calculateOffset,
      }}
      editor={editor}
      shouldShow={() => editor.isActive('pdfComponent')}
      updateDelay={0}
    >
      <BubbleMenuToolbar isDarkTheme>
        <BubbleMenuIconButton
          isDarkTheme
          active={node?.attrs['data-align'] === 'left'}
          iconName={IconMap.PhotoLeft}
          tooltip="Left"
          onClick={() => {
            editor
              ?.chain()
              .focus()
              .updateAttributes('pdfComponent', {
                'data-align': 'left',
              })
              .run();
          }}
        />
        <BubbleMenuIconButton
          isDarkTheme
          active={node?.attrs['data-align'] === 'center'}
          iconName={IconMap.PhotoCenter}
          tooltip="Center"
          onClick={() => {
            editor
              ?.chain()
              .focus()
              .updateAttributes('pdfComponent', {
                'data-align': 'center',
              })
              .run();
          }}
        />
        <BubbleMenuIconButton
          isDarkTheme
          active={node?.attrs['data-align'] === 'right'}
          iconName={IconMap.PhotoRight}
          tooltip="Right"
          onClick={() => {
            editor
              ?.chain()
              .focus()
              .updateAttributes('pdfComponent', {
                'data-align': 'right',
              })
              .run();
          }}
        />
      </BubbleMenuToolbar>
    </BubbleMenu>
  );
};
